import * as React from 'react';
import clsx from 'clsx';
import { styled, css } from '@mui/system';
import { Modal as BaseModal } from '@mui/base/Modal';
import { grey } from './colors';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  width?: string | number;
  maxHeight?: string | number;
  title?:string;
};

const Modal = ({
  open,
  onClose,
  children,
  width,
  maxHeight,
  title,
}: React.PropsWithChildren<ModalProps>) => {
  const reactId = React.useId();
  const titleId = title ? `${reactId}-title` : undefined;

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-modal="true"
      role="dialog"
      slots={{ backdrop: StyledBackdrop }}
    >
      <ModalContent
        sx={{
          width: width ?? 500,
          maxHeight: maxHeight ?? '80vh'
        }}
      >
        {title && (
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
        )}
        {children}
      </ModalContent>
    </StyledModal>
  );
};

type BackdropProps = {
  open?: boolean;
  className?: string;
};

const Backdrop = React.forwardRef<HTMLDivElement, BackdropProps>(
  (props, ref) => {
    const { open, className, ...other } = props;
    return (
      <div
        className={clsx({ 'MuiBackdrop-open': open }, className)}
        ref={ref}
        {...other}
      />
    );
  }
);

const StyledModal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    align-items: flex-start;
    padding: 16px;
  }
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled('div')(
  ({ theme }) => css`
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 500;
    text-align: start;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;

    overflow-y: auto;
    
    background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
    border-radius: 8px;
    border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    box-sizing: border-box;
    max-width: 100%;
    box-shadow: 0 4px 12px
      ${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
    padding: 24px;
    color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};

    & .modal-title {
      margin: 0;
      line-height: 1.5rem;
      margin-bottom: 8px;
    }

    & .modal-description {
      margin: 0;
      line-height: 1.5rem;
      font-weight: 400;
      color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
      margin-bottom: 4px;
    }
  `
);

Modal.displayName = 'Modal';
Backdrop.displayName = 'Backdrop';
StyledModal.displayName = 'StyledModal';
StyledBackdrop.displayName = 'StyledBackdrop';
ModalContent.displayName = 'ModalContent';

export default Modal;
